import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { type VisitorLabReport } from "@/api/visitorLabReportApi";
import { formatUsDateTime } from "@/utils/date";

interface VisitorLabReportDetailsProps {
  report: VisitorLabReport;
  onClose: () => void;
}

const statusOptions = [
  { value: 'NOT_STARTED', label: 'Not Started', color: 'bg-gray-100 text-gray-800' },
  { value: 'WAITING_FOR_RESULT', label: 'Waiting For Result', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'READY_TO_REVIEW', label: 'Ready To Review', color: 'bg-blue-100 text-blue-800' },
  { value: 'CANCELED', label: 'Canceled', color: 'bg-red-100 text-red-800' },
];

export default function VisitorLabReportDetails({ report, onClose }: VisitorLabReportDetailsProps) {
  const getStatusBadge = (status: string) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? (
      <Badge className={statusOption.color}>{statusOption.label}</Badge>
    ) : <Badge>{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-semibold">{report.testName}</h2>
          <p className="text-gray-600">Client: {report.clientName}</p>
        </div>
        {getStatusBadge(report.status)}
      </div>

      <Separator />

      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Report Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="font-medium text-sm text-gray-600">Test Order ID</label>
              <p>{report.testOrderId}</p>
            </div>
            <div>
              <label className="font-medium text-sm text-gray-600">Test Type ID</label>
              <p>{report.testTypeId}</p>
            </div>
            <div>
              <label className="font-medium text-sm text-gray-600">Client ID</label>
              <p>{report.clientId}</p>
            </div>
            <div>
              <label className="font-medium text-sm text-gray-600">Created At</label>
              <p>{formatUsDateTime(report.createdAt)}</p>
            </div>
            <div>
              <label className="font-medium text-sm text-gray-600">Updated At</label>
              <p>{formatUsDateTime(report.updatedAt)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Document Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="font-medium text-sm text-gray-600">Result Document</label>
              {report.resultDocumentUrl ? (
                <div>
                  <Button
                    variant="link"
                    className="p-0 h-auto text-blue-600"
                    onClick={() => window.open(report.resultDocumentUrl, '_blank')}
                  >
                    View Result Document
                  </Button>
                </div>
              ) : (
                <p className="text-gray-500">No result document uploaded</p>
              )}
            </div>

            <div>
              <label className="font-medium text-sm text-gray-600">Other Documents</label>
              {report.otherDocuments && report.otherDocuments.length > 0 ? (
                <div className="space-y-1">
                  {report.otherDocuments.map((doc, index) => (
                    <Button
                      key={index}
                      variant="link"
                      className="p-0 h-auto text-blue-600 block"
                      onClick={() => window.open(doc, '_blank')}
                    >
                      Document {index + 1}
                    </Button>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No additional documents</p>
              )}
            </div>

            {report.uploadedBy && (
              <div>
                <label className="font-medium text-sm text-gray-600">Uploaded By</label>
                <p>User ID: {report.uploadedBy}</p>
              </div>
            )}

            {report.uploadedAt && (
              <div>
                <label className="font-medium text-sm text-gray-600">Uploaded At</label>
                <p>{formatUsDateTime(report.uploadedAt)}</p>
              </div>
            )}

            {report.reviewedBy && (
              <div>
                <label className="font-medium text-sm text-gray-600">Reviewed By</label>
                <p>User ID: {report.reviewedBy}</p>
              </div>
            )}

            {report.reviewedAt && (
              <div>
                <label className="font-medium text-sm text-gray-600">Reviewed At</label>
                <p>{formatUsDateTime(report.reviewedAt)}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {report.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap">{report.notes}</p>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}
